import { notificationProductKey as key } from "../../utils/productUpdateNotifications";
import { alphaAdminUi } from "./alphaAdminUi";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Plus } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import service from "../../services/adminProductNotificationMappingsService";
import type { NotificationPortfolio, ProductNotificationEligibility, ProductNotificationMappingView, ProductNotificationPage, ProductNotificationSelections } from "../../types/productNotificationMappings";
import { describeNotificationError } from "../../utils/adminNotificationForm";
import { formatNumber } from "../../utils/numberUtils";
import ConfirmationModal from "../ConfirmationModal";
import ProductNotificationChannelSelector, { PRODUCT_NOTIFICATION_CHANNELS, PRODUCT_NOTIFICATION_CHANNELS_OFF } from "../notifications/ProductNotificationChannelSelector";
import AdminModalShell from "./AdminModalShell";
import EligibleNotificationPortfolioPicker from "./EligibleNotificationPortfolioPicker";

interface Props {
  user: { id: string; name?: string | null; email: string };
  onClose: () => void;
}
interface Editor {
  mode: "add" | "edit";
  product: NotificationPortfolio | null;
  selections: ProductNotificationSelections;
  eligibility: ProductNotificationEligibility;
}

const title = (product: NotificationPortfolio) => product.title || `Product #${product.productId}`;
const ineligibleReasons = {
  PRIVATE: "Private product",
  SUBSCRIPTION_REQUIRED: "Subscription required",
  PRODUCT_UNAVAILABLE: "Portfolio unavailable",
};

function EligibilityNotice({ eligibility }: { eligibility: ProductNotificationEligibility }) {
  return eligibility.eligible ? null : (
    <p className="mt-1 text-xs text-v7-bronze-dark">Ineligible: {ineligibleReasons[eligibility.reason]}. Notifications will not be sent.</p>
  );
}

// Remount on actor/target changes; closing also unmounts the body. Even a close
// and reopen of the same user gets fresh request generations and draft state.
export default function UserProductNotificationMappingsModal(props: Props) {
  const { user: actor } = useAuth();
  if (!actor?.id) return <AdminModalShell appearance="alpha" isOpen onClose={props.onClose} title="Product notifications">
    <p role="alert" className={alphaAdminUi.error}>Sign in as an administrator to manage product notifications.</p>
  </AdminModalShell>;
  return <MappingsDialog key={`${actor.id}:${props.user.id}`} {...props} />;
}

function MappingsDialog({ user, onClose }: Props) {
  const [data, setData] = useState<ProductNotificationPage<ProductNotificationMappingView> | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [blocked, setBlocked] = useState(false);
  const [editor, setEditor] = useState<Editor | null>(null);
  const [removing, setRemoving] = useState<ProductNotificationMappingView | null>(null);
  const [busy, setBusy] = useState(false);
  const [pickerRevision, setPickerRevision] = useState(0);
  const listRequest = useRef(0);
  const listAbort = useRef<AbortController | null>(null);
  const mutationRequest = useRef(0);
  const busyRef = useRef(false);

  const invalidateList = useCallback(() => {
    ++listRequest.current;
    listAbort.current?.abort();
  }, []);

  const load = useCallback(async (requestedPage: number) => {
    invalidateList();
    const token = listRequest.current;
    const controller = new AbortController();
    listAbort.current = controller;
    const current = () => token === listRequest.current && !controller.signal.aborted;
    setLoading(true);
    setData(null);
    setListError(null);
    setPage(requestedPage);
    try {
      let result = await service.list(user.id, requestedPage, controller.signal);
      if (!current()) return;
      // Deletion (including by another admin) can empty the final page. Fetch
      // the new last page rather than leaving an empty, out-of-range view.
      while (result.currentPage > Math.max(1, result.totalPages)) {
        result = await service.list(user.id, Math.max(1, result.totalPages), controller.signal);
        if (!current()) return;
      }
      setPage(result.currentPage);
      setData(result);
      setBlocked(false);
    } catch (err) {
      if (!current()) return;
      const detail = describeNotificationError(err);
      setListError(detail.message);
      setBlocked([401, 403, 404].includes(detail.status ?? 0));
    } finally {
      if (current()) setLoading(false);
    }
  }, [invalidateList, user.id]);

  useEffect(() => {
    void load(1);
    const invalidateMutation = () => { ++mutationRequest.current; };
    return () => { invalidateList(); invalidateMutation(); };
  }, [load, invalidateList]);

  const mutate = async (operation: "save" | "remove") => {
    const product = operation === "save" ? editor?.product : removing?.product;
    if (!product || busyRef.current || blocked) return;
    busyRef.current = true;
    setBusy(true);
    setMutationError(null);
    setNotice(null);
    // No read started before a write may subsequently restore the old rows.
    invalidateList();
    const token = ++mutationRequest.current;
    const current = () => token === mutationRequest.current;
    try {
      if (operation === "save" && editor) await service.upsert(user.id, product.productId, editor.selections, product.productType);
      else await service.remove(user.id, product.productId, product.productType);
      if (!current()) return;
      setEditor(null);
      setRemoving(null);
      setNotice(operation === "save" ? "Product notification selections saved." : "Product notification mapping removed.");
      // load owns its error state: a failed refresh must not make a successful
      // write appear to have failed or encourage a duplicate submission.
      await load(page);
    } catch (err) {
      if (!current()) return;
      const detail = describeNotificationError(err);
      if (detail.ambiguous) {
        setEditor(null);
        setRemoving(null);
        setNotice("The change could not be confirmed. Check the refreshed mappings before making another change.");
        await load(page);
      } else if (detail.status === 409) {
        setMutationError("This product is no longer eligible for a new mapping. The list has been refreshed; choose an eligible product or edit a retained mapping.");
        setEditor(previous => previous ? { ...previous, mode: "add", product: null, eligibility: { eligible: true, reason: null } } : null);
        setPickerRevision(value => value + 1);
        await load(page);
      } else {
        setMutationError(detail.message);
        if ([401, 403, 404].includes(detail.status ?? 0)) setBlocked(true);
      }
    } finally {
      if (current()) { busyRef.current = false; setBusy(false); setLoading(false); }
    }
  };

  const close = () => { if (!busyRef.current) onClose(); };
  return <>
    <AdminModalShell appearance="alpha" isOpen onClose={close} closeDisabled={busy || Boolean(removing)} title="Product notifications" maxWidthClassName="max-w-4xl"
      description={<span className="ph-mask">{user.name || user.email}{user.name && ` (${user.email})`}</span>}>
      <div className="space-y-4">
        {notice && <p role="status" className={alphaAdminUi.warning}>{notice}</p>}
        {listError && <div role="alert" className={alphaAdminUi.error}>
          Could not refresh product mappings: {listError} <button type="button" className={alphaAdminUi.secondary} disabled={busy} onClick={() => void load(page)}>Retry mappings</button>
        </div>}
        {mutationError && !removing && <p role="alert" className={alphaAdminUi.error}>{mutationError}</p>}
        {editor ? <form className="space-y-4" onSubmit={event => { event.preventDefault(); void mutate("save"); }}>
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-base font-bold text-v7-ink">{editor.mode === "add" ? "Add product" : "Edit notifications"}</h3>
            <button type="button" className={alphaAdminUi.secondary} disabled={busy} onClick={() => { setEditor(null); setMutationError(null); }}>Back to mappings</button>
          </div>
          {editor.mode === "add" ? <>
            <p className="text-sm text-v7-ink-soft">If this product is already configured, saving replaces its channel selections with the choices below.</p>
            <EligibleNotificationPortfolioPicker key={pickerRevision} userId={user.id} value={editor.product} disabled={busy || blocked}
              onChange={product => setEditor(previous => previous ? { ...previous, product } : null)} />
          </> : <div>
            <p className="text-sm font-semibold text-v7-ink">{editor.product && title(editor.product)}</p>
            <p className="text-xs text-v7-ink/60">{editor.product?.provider?.name || "Provider unavailable"}</p>
            <EligibilityNotice eligibility={editor.eligibility} />
          </div>}
          <ProductNotificationChannelSelector appearance="alpha" value={editor.selections} disabled={busy || blocked}
            onChange={selections => setEditor(previous => previous ? { ...previous, selections } : null)} />
          <button type="submit" className={alphaAdminUi.primary} disabled={busy || blocked || !editor.product}>{busy ? "Saving..." : "Save selections"}</button>
        </form> : <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-v7-ink-soft">Manage this user's product update channels.</p>
            <button type="button" className={alphaAdminUi.primary} disabled={busy || blocked || loading || !data} onClick={() => {
              setMutationError(null); setNotice(null);
              setEditor({ mode: "add", product: null, selections: { ...PRODUCT_NOTIFICATION_CHANNELS_OFF }, eligibility: { eligible: true, reason: null } });
            }}><Plus className="h-4 w-4" aria-hidden="true" />Add product</button>
          </div>
          {loading && <p role="status" className="text-sm text-v7-ink/60">Loading product mappings...</p>}
          {data && <>
            {data.items.length === 0 ? <p className="rounded-v7-panel border border-v7-ink/15 py-10 text-center text-sm text-v7-ink/60">No product notification mappings.</p> : (
              <ul className="divide-y divide-v7-ink/15 rounded-v7-panel border border-v7-ink/15">
                {data.items.map(view => <li key={key(view.product)} className="flex flex-col items-start justify-between gap-3 bg-v7-white p-4 sm:flex-row">
                  <div className="min-w-0 flex-1">
                    <h3 className="break-words text-sm font-semibold text-v7-ink">{title(view.product)}</h3>
                    <p className="text-xs text-v7-ink/60">{view.product.productType === "BASKET" ? "Basket" : "Portfolio"} - {view.product.provider?.name || "Provider unavailable"}</p>
                    <p className="mt-2 text-sm text-v7-ink-soft">{view.mapping
                      ? PRODUCT_NOTIFICATION_CHANNELS.filter(channel => view.mapping?.[channel.key]).map(channel => channel.label).join(", ") || "Off"
                      : "Mapping unavailable. Refresh to check its status."}</p>
                    <EligibilityNotice eligibility={view.eligibility} />
                  </div>
                  <div className="flex gap-2">
                    <button type="button" className={alphaAdminUi.secondary} aria-label={`Edit ${title(view.product)}`} disabled={busy || blocked || !view.mapping} onClick={() => {
                      if (!view.mapping) return;
                      setNotice(null); setMutationError(null);
                      setEditor({ mode: "edit", product: view.product, selections: { ...view.mapping }, eligibility: view.eligibility });
                    }}>Edit</button>
                    <button type="button" className={alphaAdminUi.danger} aria-label={`Remove ${title(view.product)}`} disabled={busy || blocked} onClick={() => { setMutationError(null); setRemoving(view); }}>Remove</button>
                  </div>
                </li>)}
              </ul>
            )}
            <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-v7-ink/60" aria-label="Mapping pagination">
              <span>{formatNumber(data.totalItems, 0)} products. Page {formatNumber(page, 0)} of {formatNumber(Math.max(1, data.totalPages), 0)}</span>
              <div className="flex gap-2">
                <button type="button" className={alphaAdminUi.secondary} disabled={busy || page <= 1} onClick={() => void load(page - 1)}>Previous mappings</button>
                <button type="button" className={alphaAdminUi.secondary} disabled={busy || page >= data.totalPages} onClick={() => void load(page + 1)}>Next mappings</button>
              </div>
            </div>
          </>}
        </>}
        <div className="flex justify-end"><button type="button" className={alphaAdminUi.secondary} disabled={busy || Boolean(removing)} onClick={close}>Close</button></div>
      </div>
    </AdminModalShell>
    <ConfirmationModal appearance="alpha" isOpen={Boolean(removing)} onClose={() => { if (!busyRef.current) { setRemoving(null); setMutationError(null); } }}
      onConfirm={() => void mutate("remove")} title="Remove product notifications?" confirmText="Remove mapping" isDestructive loading={busy} confirmDisabled={blocked}
      message={<div className="space-y-3"><p>Remove notification selections for {removing && title(removing.product)}? This does not change the user's subscription.</p>
        {mutationError && <p role="alert" className={alphaAdminUi.error}>{mutationError}</p>}</div>} />
  </>;
}
